import React from 'react'
import { api, type Coach, type Plan } from '../api/client'
import { screenCache } from '../api/screenCache'
import { useApp } from '../context/AppContext'
import { goalsDraft } from '../plan/goalsDraft'
import type { GoalDraft } from '../plan/planForm'
import type { FreedomCard } from '../types/app'
import { FIRST_TIME_HEADING, NO_MENU_MESSAGE, goalHeadline, goalSubline } from '../plan/wording'
import FreedomLine from './goals/FreedomLine'
import GoalFieldsForm, { useGoalFields } from './goals/GoalFields'
import GoalSheet from './goals/GoalSheet'
import ThisWeekCard, { hasThisWeek } from './goals/ThisWeekCard'
import DreamWeekCard, { DreamWeekPreview } from './goals/DreamWeekCard'
import { CoachPreview } from './goals/CoachPreview'
import { usePlanWeek } from './goals/usePlanWeek'
import { useFreedom } from './goals/useFreedom'
import ProLock from '../billing/ProLock'
import { HINT_GOALS } from '../billing/rungs'
import { useBilling } from '../billing/useBilling'
import { saveErrorMessage, showToast } from './settings/toast'
import './Goals.css'
import { IconButton } from '../ui/Icon'
import { Loading } from '../ui/States'

// Goals is one screen: the goal, what her Dream Week adds up to, and how the
// month is really going. First time through, it asks for the goal on one card
// before showing the rest.
export default function Goals() {
  const { state, dispatch } = useApp()
  const { coachLocked } = useBilling()
  const cachedPlan = screenCache.get<Plan>('plan')
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>(() => (cachedPlan ? 'ready' : 'loading'))
  const [plan, setPlan] = React.useState<Plan | null>(() => cachedPlan ?? null)
  // The goal entered on the first-time card, kept as a draft until the first
  // save carries it with the week: without it, a tab switch before saving
  // would lose those numbers even though the week draft survives.
  const [draftGoal, setDraftGoal] = React.useState<GoalDraft | null>(() => goalsDraft.goal())
  // Set once the first load of this visit has settled (see settle below).
  const [sheetOpen, setSheetOpen] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [saveError, setSaveError] = React.useState('')
  // coachVersion forces the Coach to refetch only when the plan actually
  // changes underneath it (saved or applied), not on every render.
  const [coachVersion, setCoachVersion] = React.useState(0)
  const [coach, setCoach] = React.useState<Coach | null>(() => screenCache.get<Coach>('coach') ?? null)
  const [coachFailed, setCoachFailed] = React.useState(false)

  // Every place that gets a server-fresh plan — the initial load, and every
  // save/apply below — goes through here, so the cache is never left holding
  // a pre-mutation plan: without this, switching tabs right after a save and
  // back would show stale data until the next background refetch landed.
  const applyFreshPlan = React.useCallback((next: Plan) => {
    setPlan(next)
    screenCache.set('plan', next)
  }, [])

  // The week is built once, from the plan it opens on, and a week draft is
  // checked against that plan's windows: built from a cached plan, it would
  // keep hours that have since changed (in Settings, or on another device)
  // and offer to save them back over the new ones. So the screen waits for
  // this visit's first fetch; only if that fails does the cached plan stand
  // in, so she can still see her plan offline.
  const [fresh, setFresh] = React.useState(false)
  const settled = React.useRef(false)
  const settle = React.useCallback((p: Plan) => {
    if (settled.current) return
    settled.current = true
    setFresh(true)
    // She was part-way through changing her saved goal: the sheet reopens.
    // Never on the first-time card, where there is no saved goal to change.
    setSheetOpen(p.goal !== null && goalsDraft.setup('edit') !== null)
  }, [])

  // A failed background refresh keeps the cached plan instead of swapping in
  // the error screen.
  const load = React.useCallback(async (quiet = false) => {
    if (!quiet) setStatus('loading')
    try {
      const p = await api.getPlan()
      applyFreshPlan(p)
      settle(p)

      // Once the plan actually holds a goal — saved here or from another
      // tab — a leftover first-time goal draft is stale: without this it
      // would come back as the goal if the saved one were ever read as empty.
      if (p.goal) {
        goalsDraft.clearGoal()
        setDraftGoal(null)
      }
      setStatus('ready')
    } catch {
      const cached = screenCache.get<Plan>('plan')
      if (quiet && cached) settle(cached)
      else if (!quiet) setStatus('error')
    }
  }, [applyFreshPlan, settle])

  React.useEffect(() => {
    load(Boolean(cachedPlan))
    // Only on mount: cachedPlan is read once, at the moment this instance
    // was created, to decide whether this first load should be quiet.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  // The Coach is fetched in parallel with the plan above, not after it: the
  // week bar never waits on it. coachVersion bumps when the plan or goal is
  // saved or applied, which is the only other time it needs to refetch.
  //
  // A request token makes sure only the latest request updates state and the
  // cache: two overlapping fetches (a plan saved twice in quick succession)
  // can resolve out of order, and the older, staler Coach must never land
  // after a newer one.
  const coachLatest = React.useRef(0)
  const loadCoach = React.useCallback(async () => {
    const request = ++coachLatest.current
    try {
      const c = await api.getCoach()
      if (request !== coachLatest.current) return
      setCoach(c)
      setCoachFailed(false)
      screenCache.set('coach', c)
    } catch {
      if (request === coachLatest.current) setCoachFailed(true)
    }
  }, [])

  React.useEffect(() => {
    loadCoach()
  }, [loadCoach, coachVersion])

  // The Freedom Score is fetched in parallel like the Coach, never holding
  // the plan back.
  const freedom = useFreedom()

  // Adding a treatment from the no-services gate's "Open my menu" happens in
  // Settings, off this screen entirely: without this, coming back still shows
  // the gate against the plan fetched before she had any services. Quiet, so
  // it doesn't flash a loading state over whatever she's looking at.
  const prevSettingsPanel = React.useRef(state.settingsPanel)
  React.useEffect(() => {
    if (prevSettingsPanel.current === 'services' && state.settingsPanel === null) {
      load(true)
    }
    prevSettingsPanel.current = state.settingsPanel
  }, [state.settingsPanel, load])

  const goal = plan?.goal ?? draftGoal
  const firstTime = goal === null

  // The first-time card and the plan share one scroll container: moving from
  // one to the other must not leave the plan scrolled to the card's position.
  React.useEffect(() => {
    document.querySelector('.main-content')?.scrollTo({ top: 0 })
  }, [firstTime])

  if (status !== 'ready' || !plan || !fresh) {
    return (
      <div className="screen goals-screen">
        <h1 className="eyebrow">Goals</h1>
        {status === 'error' ? (
          <>
            <p className="goals-muted">Couldn’t load your plan. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={() => load()}>
              Try again
            </button>
          </>
        ) : (
          <Loading />
        )}
      </div>
    )
  }

  // Off Pro the screen is locked whole (owner, 2026-10-24): her working
  // hours as a faded Dream Week, then the Coach's card (her own reading when
  // it has one, else a marked example), with no goal, This week or Freedom
  // Score, even when a goal is saved (a lapsed Pro) or a score is cached.
  if (coachLocked) {
    return (
      <div className="screen goals-screen">
        <h1 className="eyebrow">Goals</h1>
        <ProLock hint={HINT_GOALS} lock="dream_week">
          <DreamWeekPreview plan={plan} />
          <CoachPreview coach={coach} />
        </ProLock>
      </div>
    )
  }

  if (goal === null) {
    return (
      <FirstTimeCard
        noMenu={plan.inputs.mix.length === 0}
        onOpenMenu={() => dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'services' })}
        // "Design my week" doesn't save yet: the first save carries the goal
        // and the week together.
        onDone={(next) => {
          goalsDraft.saveGoal(next)
          setDraftGoal(next)
        }}
      />
    )
  }

  // A draft goal is only changed on this device until the first save; a
  // saved one is saved at once and keeps her week, saved or not.
  const saveGoal = async (next: GoalDraft) => {
    if (!plan.goal) {
      goalsDraft.saveGoal(next)
      setDraftGoal(next)
      setSheetOpen(false)
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      applyFreshPlan(await api.putPlan({ ...next, windows: plan.windows }))
      setCoachVersion((v) => v + 1)
      setSheetOpen(false)
      showToast(dispatch, 'Goal saved')
    } catch (e) {
      setSaveError(saveErrorMessage(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <PlanScreen
        plan={plan}
        goal={goal}
        coach={coach}
        freedom={freedom}
        coachFailed={coachFailed}
        onRetryCoach={loadCoach}
        onChangeGoal={() => setSheetOpen(true)}
        // Once the plan actually holds a goal, the first-time goal draft has
        // done its job: what the server has is now the source of truth.
        onPlanSaved={(next) => {
          applyFreshPlan(next)
          setCoachVersion((v) => v + 1)
          if (next.goal) {
            goalsDraft.clearGoal()
            setDraftGoal(null)
          }
        }}
      />
      {sheetOpen && (
        <GoalSheet
          goal={goal}
          saving={saving}
          error={saveError}
          onSave={saveGoal}
          onClose={() => {
            setSheetOpen(false)
            setSaveError('')
          }}
        />
      )}
    </>
  )
}

// FirstTimeCard asks for the goal before there is anything to plan against.
function FirstTimeCard({ noMenu, onOpenMenu, onDone }: { noMenu: boolean; onOpenMenu: () => void; onDone: (goal: GoalDraft) => void }) {
  const { fields, errors, set, submit } = useGoalFields(null, 'first')
  const done = (e: React.FormEvent) => {
    e.preventDefault()
    const goal = submit()
    if (goal) onDone(goal)
  }
  return (
    <div className="screen goals-screen">
      <h1 className="eyebrow">Goals</h1>
      <form className="goal-card goals-first" onSubmit={done} noValidate>
        <h2 className="goals-headline">{FIRST_TIME_HEADING}</h2>
        <GoalFieldsForm fields={fields} errors={errors} onChange={set} />
        {/* Without a treatment on her menu there is nothing to plan with:
            the way on is the menu, not the week. */}
        {noMenu ? (
          <>
            <p className="goals-info" role="status">
              {NO_MENU_MESSAGE}
            </p>
            <button type="button" className="btn btn-primary btn-block" onClick={onOpenMenu}>
              Open my menu
            </button>
          </>
        ) : (
          <button type="submit" className="btn btn-primary btn-block">
            Design my week
          </button>
        )}
      </form>
    </div>
  )
}

interface PlanScreenProps {
  plan: Plan
  goal: GoalDraft
  coach: Coach | null
  freedom: FreedomCard | null
  coachFailed: boolean
  onRetryCoach: () => void
  onChangeGoal: () => void
  onPlanSaved: (plan: Plan) => void
}

// PlanScreen is the goal, then what changes week to week (the This week card:
// the Freedom Score, the month and the coach) once there's any, then the
// Dream Week with what it would take home. Before there's a week to check,
// the Freedom Score's waiting row sits under the Dream Week instead, so a
// first visit opens on the goal and the week.
function PlanScreen({ plan, goal, coach, freedom, coachFailed, onRetryCoach, onChangeGoal, onPlanSaved }: PlanScreenProps) {
  const [selected, setSelected] = React.useState<number | null>(null)
  const week = usePlanWeek({
    plan,
    goal,
    onPlanSaved,
    onInvalidDay: (day) => {
      setWeekOpen(true)
      setSelected(day)
    },
  })
  // The Dream Week card starts open unless the week already is her working
  // hours with nothing to save. A day that couldn't be saved opens it too.
  const [weekOpen, setWeekOpen] = React.useState(() => week.unsaved || !plan.live || plan.windows.length === 0)

  const thisWeek = hasThisWeek(freedom, coach)

  return (
    <div className="screen goals-screen">
      <h1 className="eyebrow">Goals</h1>
      <div className="goals-head">
        <div>
          <p className="goals-headline">{goalHeadline(goal)}</p>
          <p className="goals-muted">{goalSubline(goal)}</p>
        </div>
        <IconButton icon="edit" label="Change your goal" size={18} onClick={onChangeGoal} />
      </div>
      {thisWeek && (
        <ThisWeekCard
          freedom={freedom}
          goal={goal}
          coach={coach}
          coachFailed={coachFailed}
          onRetryCoach={onRetryCoach}
          editing={week.unsaved}
          now={new Date()}
        />
      )}
      <DreamWeekCard
        plan={plan}
        goal={goal}
        week={week}
        selected={selected}
        onSelect={setSelected}
        open={weekOpen}
        onToggle={() => setWeekOpen((value) => !value)}
      />
      {!thisWeek && <FreedomLine freedom={freedom} />}
    </div>
  )
}
